import { DisplayNameMapping } from "@/permissions";
import { useApiV1UserManagementProfileUpdateMutation, useWhoamiRetrieveQuery } from "@/services/api";
import { UserManagementRead } from "@/services/generatedApi";
import { Loader } from "@/ui-components/Loader";
import Input from "@/ui-components/forms/Input";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormLabel,
} from "@mui/material";
import React from "react";
import { Form } from "react-final-form";
import { useTranslation } from "react-i18next";

type ProfileModalProps = {
  isOpen: boolean;
  toggleIsOpen: () => void;
};

type FormData = Pick<UserManagementRead, "first_name" | "last_name" | "job_title">;

export const ProfileModal: React.FC<ProfileModalProps> = ({ isOpen, toggleIsOpen }) => {
  const { t } = useTranslation();
  const { data, isLoading } = useWhoamiRetrieveQuery();
  const [updateUser, { isLoading: isSaving }] = useApiV1UserManagementProfileUpdateMutation();
  const onSubmit = (user: FormData) => {
    updateUser({
      userProfile: {
        first_name: user.first_name,
        last_name: user.last_name,
        job_title: user.job_title,
      },
    })
      .unwrap()
      .then(toggleIsOpen);
  };

  if (isLoading || !data) {
    return null;
  }
  const groups = data.groups.map(name => DisplayNameMapping[name] || name);
  const initialData = {
    first_name: data.first_name,
    last_name: data.last_name,
    job_title: data.job_title,
    email: data.email,
    groups,
  };
  return (
    <Dialog
      open={isOpen}
      onClose={toggleIsOpen}
      aria-labelledby={t("profile.Profile")}
      aria-describedby={t("profile.Edit_your_profile")}
    >
      <DialogTitle>{t("profile.Profile")}</DialogTitle>
      <DialogContent
        sx={{
          px: "16px",
          width: "600px",
        }}
      >
        <Form<FormData>
          onSubmit={onSubmit}
          initialValues={initialData}
          render={({ handleSubmit }) => (
            <>
              {isSaving ? (
                <Loader
                  sx={{
                    height: "100%",
                  }}
                />
              ) : (
                <form
                  id="user_profile_form"
                  onSubmit={async event => {
                    const response = await handleSubmit(event);
                    if (!response) {
                      return;
                    }
                    toggleIsOpen();
                  }}
                  noValidate
                >
                  <Box
                    sx={{
                      borderRadius: "8px",
                      p: "16px",
                      pb: 0,
                      border: "1px solid var(--green-grey-scale-cold-tones-cold-tones-200)",
                    }}
                  >
                    <Input
                      label={t("general.First_name")}
                      id="first_name"
                      name="first_name"
                      required
                      fieldProps={{
                        validate: value => (value ? undefined : "This field is required."),
                      }}
                    />
                    <Input
                      label={t("general.Surname")}
                      id="last_name"
                      name="last_name"
                      required
                      fieldProps={{
                        validate: value => (value ? undefined : "This field is required."),
                      }}
                    />
                    <Input label={t("general.Job_title")} id="job_title" name="job_title" />
                    <FormControl sx={{ width: "450px", pb: "25px" }}>
                      <FormLabel
                        sx={{
                          color: "var(--grey-scale-800)",
                          fontSize: "12px",
                          fontWeight: "400",
                          lineHeight: "16px",
                          mb: 0,
                        }}
                      >
                        <>{groups.length > 1 ? t("general.Roles") : t("general.Role")}</>
                      </FormLabel>
                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                        }}
                      >
                        {groups.map(group => (
                          <Chip label={group} sx={{ mr: "8px", mb: "8px" }} key={group} />
                        ))}
                      </Box>
                    </FormControl>
                    <Input required label={t("general.Email")} id="email" name="email" disabled />
                  </Box>
                </form>
              )}
            </>
          )}
        />
      </DialogContent>
      <DialogActions
        sx={{
          borderTop: "1px solid var(--green-grey-scale-cold-tones-cold-tones-100)",
          justifyContent: "flex-end",
          width: "100%",
          display: "flex",
        }}
      >
        <Button variant="outlined" onClick={toggleIsOpen}>
          {t("general.Cancel")}
        </Button>
        <Button variant="contained" disabled={isSaving} form="user_profile_form" type="submit">
          {t("general.Save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
